import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Text, IconButton, TooltipHost } from 'office-ui-fabric-react';
import get from 'lodash/get';
import { differenceBy, size, drop } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import SearchBar from '../../components/SearchBar';
import Link from '../../components/Link';
import TextButton from '../../components/TextButton';
import SavingIndicator from '../../components/SavingIndicator';
import { searchByField, clearSearchResults } from '../../actions/search';
import { fetchDimensionListDetails } from '../../actions/dimensionList';
import theme from '../../theme.style';
import styles from './AddDimensionListSearch.style';
import {
  dimensionListTypes,
  dLTypeValues,
  individualItemsColumnHeaders,
  listColumnHeaders,
  stagingColumnHeaders,
  filterOptions,
} from '../../constants';
import DimensionSearchList from '../../components/DimensionSearchList';

const AddDimensionListSearch = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const listTypeTitle = location.pathname.split('/').pop();
  const { listFormData } = useLocation();
  const [listType, setListType] = useState('product');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [totalCount, setTotalCount] = React.useState(0);
  const [selectAllItems, setSelectAllItems] = useState(false);
  const [filterStates, setFilterStates] = useState([{ filter: '', query: '' }]);
  const [sortAscending, setSortAscending] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [loading, setLoading] = useState(true);
  const [isListSearch, setIsListSearch] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [listId, setListId] = useState();
  const [page, setPage] = useState(1);
  const [resultsObject, setResultsObject] = useState('search.results');
  const history = useHistory();

  const searchResults = useSelector(state => Object.values(get(state, resultsObject, {}))).filter(
    result => result.id !== listId,
  );
  const observer = useRef();

  useEffect(() => {
    if (!listFormData)
      history.push({
        pathname: '/dimensionLists/add',
      });
    else {
      const { listItems } = listFormData;
      setListType(listFormData.listType);
      setListId(listFormData.listId);
      if (!isEmpty(listItems)) {
        setSelectedItems(listItems);
        setLoading(false);
      }
    }
  }, [listFormData]);

  const handleSearch = async (paginationData = null, onScroll = '') => {
    let resource = dLTypeValues[listTypeTitle]?.route;
    setLoading(true);
    setResultsObject(dLTypeValues[listTypeTitle]?.results);
    // if filters are empty, do not show table
    const showSearch = filterStates.some(term => term.query !== '');
    setShowSearchResults(showSearch);

    const isQueryInvalid =
      filterStates.some(
        term => term.filter === 'productTokenId' && term.query && isNaN(parseFloat(term.query)),
      ) || !filterStates.some(term => term.filter && term.query);

    if (showSearch && !isQueryInvalid) {
      const filtersWithQuery = filterStates.filter(term => term.query);
      const queryString = filtersWithQuery
        .map(term => {
          // search query for list searches
          if (isListSearch) {
            resource = 'dimensionLists';
            if (
              ['productTokenId', 'productTypeId', 'activityTypeId', 'saleTypeId'].includes(
                term.filter,
              )
            )
              return `itemId=${term.query}`;
          }
          // search query for individual item searches
          return `${term.filter}=${term.query}`;
        })
        .join('&')
        .concat('&isDimensionSearch=true')
        .concat(isListSearch ? `&listType=${listType}` : '')
        .concat(sortColumn ? `&sort=${sortAscending ? '+' : '-'}${sortColumn}` : '');

      const response = await dispatch(
        searchByField([resource], queryString, {
          start:
            paginationData && paginationData.currentPageIndex
              ? paginationData.currentPageIndex * paginationData.pageLimit
              : 0,
          count: paginationData?.pageLimit ? paginationData.pageLimit : 20,
        }),
      );
      if (!onScroll) {
        setPage(1);
      }

      setTotalCount(get(response, `meta.totalCount`, 0));
    } else {
      dispatch(clearSearchResults());
      setPage(1);
    }
    setLoading(false);
  };

  const handleSearchInput = (query, index) => {
    const newFilterState = filterStates;
    newFilterState[index].query = query;
    setFilterStates(newFilterState);

    handleSearch();
  };

  const handleSearchClear = index => {
    const newFilterState = filterStates;
    newFilterState[index].query = '';
    setFilterStates(newFilterState);
    handleSearch();
  };

  const handleFilter = async (event, option, index) => {
    if (filterStates[index].filter !== option.key) {
      const newFilterState = filterStates;

      // Check if users has selected a search field that is only available for list searches
      if (option.key === 'listName') setIsListSearch(true);
      newFilterState[index].filter = option.key;
      setFilterStates(newFilterState);

      // Check if user is no longer looking for lists
      const listSearch = filterStates.some(filterState => {
        return filterState.filter === 'listName';
      });
      if (!listSearch) setIsListSearch(false);

      // Clears the search query when changing filter types
      if (filterStates[index].query !== '') {
        const searchContainer = event.target.closest('[aria-label="filterAndSearchContainer"]');
        const searchClearBtn = searchContainer.querySelector('[aria-label="Clear text"]');
        searchClearBtn.click();
      } else handleSearch();
    }
  };

  const handleAdditionalSearchFields = () => {
    const newFilterState = filterStates;
    newFilterState.push({ filter: '', query: '' });
    setFilterStates(newFilterState);
    handleSearch();
  };

  const handleRemoveSearchFields = index => {
    const newFilterState = filterStates;
    newFilterState.splice(index, 1);
    setFilterStates(newFilterState);
    handleSearch();
  };

  // load next set of data when last item is visible
  const lastItemRef = useCallback(
    refElem => {
      if (loading || !refElem) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (
          entries[0].isIntersecting &&
          searchResults?.length < totalCount &&
          page < Math.ceil(totalCount / 20)
        ) {
          const data = { currentPageIndex: page, pageLimit: 20 };
          handleSearch(data, 'onScroll');
          setPage(prevPage => prevPage + 1);
        }
      });
      if (refElem) observer.current.observe(refElem);
    },
    [loading, page],
  );

  const handleColumnClick = (event, column) => {
    setSortAscending(!sortAscending);
    setSortColumn(column.fieldName);
  };

  const handleSelectAllButton = async () => {
    const items = showSearchResults ? searchResults : '';
    const newItems = items.filter(item => !selectedItems.some(selected => selected.id === item.id));
    setSelectedItems([...selectedItems, ...newItems]);
    setSelectAllItems(true);
  };

  const handleDeselectAndRemoveAll = async () => {
    if (listFormData) setSelectedItems([...listFormData.listItems]);
    else setSelectedItems([]);
    setSelectAllItems(false);
  };

  const onHandleColumnClick = () => {
    if (selectAllItems) {
      if (listFormData) setSelectedItems([...listFormData.listItems]);
      else setSelectedItems([]);
      setSelectAllItems(false);
    } else {
      const items = showSearchResults ? searchResults : '';
      const newItems = items.filter(
        item => !selectedItems.some(selected => selected.id === item.id),
      );
      setSelectedItems([...selectedItems, ...newItems]);
      setSelectAllItems(true);
    }
  };

  const onSelectItems = item => {
    const selectedItem = item;
    if (item.isList) {
      dispatch(fetchDimensionListDetails(item.id, { useCache: false })).then((result, err) => {
        // eslint-disable-next-line no-console
        if (err) console.error(err);
        selectedItem.listHierarchy =
          result.payload?.entities?.dimensionListDetails[item.id]?.listItems;
      });
    }
    if (
      selectedItems.length > 0 &&
      selectedItems.some(checkedItem => checkedItem.name === selectedItem.name)
    ) {
      setSelectedItems(selectedItems.filter(checkedItem => checkedItem.name !== selectedItem.name));
      // Uncheck the column header select products if the selected products lengh becomes zero.
      setSelectAllItems(false);
    } else {
      setSelectedItems([...selectedItems, selectedItem]);
      if (selectedItems.length === totalCount - 1) {
        setSelectAllItems(true);
      }
    }
  };

  // change
  const newlySelectedProducts = listFormData
    ? differenceBy(selectedItems, listFormData.listItems)
    : [];

  const availableFilterOptions = index => {
    let availableOptions = listType && [...filterOptions[listType]];
    const firstField =
      listType && filterOptions[listType].find(filter => filter.key === filterStates[0].filter);
    // allow the first field the ability to switch between list search and individual search
    if (index > 0 && firstField) {
      // if list search, only show list options
      if (isListSearch) {
        availableOptions = availableOptions.filter(option => option.isListOption === true);
      } else if (!firstField.isListOption) {
        availableOptions = availableOptions.filter(option => option.key !== 'listName');
      }
    }
    // remove fields that have already been selected
    const alreadySelectedFilters = filterStates
      .map(e => e.filter)
      .filter(e => e !== '' && e !== filterStates[index].filter);
    availableOptions =
      listType && availableOptions.filter(option => !alreadySelectedFilters.includes(option.key));

    return availableOptions;
  };

  const previousPathName = listId ? `/dimensionlists/${listId}/edit` : '/dimensionLists/add';

  // handle adding items to the staged list
  const handleSave = async () => {
    setIsSubmitting(true);

    // change
    // Set Hierarchy - default to "This Item Only" for now
    const selectedProductsWithHierarchy = selectedItems.map(item => {
      if (!item.isList && listType === 'product') return { ...item, hierarchy: 'This Item Only' };
      return item;
    });
    history.push({
      pathname: previousPathName,
      listFormData: { ...listFormData, listItems: selectedProductsWithHierarchy },
    });
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    history.push({ pathname: previousPathName, listFormData });
  };

  const selectMenuProps = {
    items: [
      {
        key: `select${listType}Summary`,
        text: `Select To Add ${listType} to dimension list`,
        iconProps: { iconName: 'LocationCircle' },
      },
    ],
  };

  const viewIndividualItemsColumn = {
    key: 'view',
    name: 'View',
    isResizable: false,
    minWidth: 20,
    maxWidth: 20,
    isPadded: true,
    styles: { root: { visibility: 'hidden' } },
    onRender: item => {
      return (
        <div className={styles.viewButton}>
          <Link to={`/products/${item.id}`} target="_blank">
            View
          </Link>
        </div>
      );
    },
  };

  const viewListColumn = {
    key: 'view',
    name: 'View',
    isResizable: false,
    styles: { root: { visibility: 'hidden' } },
    minWidth: 20,
    maxWidth: 20,
    isPadded: true,
    onRender: item => {
      return (
        <div className={styles.viewButton}>
          <Link to={`/dimensionLists/${item.id}/product`} target="_blank">
            View
          </Link>
        </div>
      );
    },
  };

  const viewStagingColumn = {
    key: 'view',
    name: 'View',
    isResizable: false,
    headerClassName: styles.iconColumnHeader,
    styles: { root: { visibility: 'hidden' } },
    minWidth: 25,
    maxWidth: 25,
    onRender: item => {
      if (item.isList) {
        return (
          <div className={styles.viewButton}>
            <Link to={`/dimensionLists/${item.id}/product`} target="_blank">
              View
            </Link>
          </div>
        );
      }
      return (
        <div className={styles.viewButton}>
          <Link to={`/products/${item.id}`} target="_blank">
            View
          </Link>
        </div>
      );
    },
  };

  const individualItemsColumns = [
    {
      key: 'select',
      name: 'select',
      headerClassName: styles.iconColumnHeaderSelectButton,
      arianame: 'icon',
      iconName: selectAllItems ? 'SkypeCircleCheck' : '',
      isIconOnly: true,
      fieldName: 'select',
      minWidth: 20,
      maxWidth: 20,
      onColumnClick: onHandleColumnClick,
      onRender: (item, index) => {
        const itemName = get(item, 'name');
        const iconName = selectedItems.some(items => {
          let selected = false;
          const someItemsName = get(items, 'name');
          if (someItemsName === itemName) selected = true;
          else if (selectedItems && selectedItems.length > 0)
            selectedItems.forEach(_item => {
              if (_item.listHierarchy && _item.listHierarchy.length > 0)
                _item.listHierarchy.forEach(heirarchyItem => {
                  if (itemName === heirarchyItem.name) {
                    selected = true;
                  }
                });
            });
          return selected;
        })
          ? 'CompletedSolid'
          : '';
        const alreadyIncluded =
          listFormData &&
          listFormData.listItems.some(items => {
            let selected = false;
            const someItemsName = get(items, 'name');
            if (someItemsName === itemName) selected = true;
            else if (selectedItems && selectedItems.length > 0)
              selectedItems.forEach(_item => {
                if (_item.listHierarchy && _item.listHierarchy.length > 0)
                  _item.listHierarchy.forEach(heirarchyItem => {
                    if (itemName === heirarchyItem.name) {
                      selected = true;
                    }
                  });
              });
            return selected;
          });
        return (
          <TooltipHost
            onTooltipToggle={false}
            content={alreadyIncluded ? `Item Included` : ''}
            calloutProps={{
              gapSpace: 0,
            }}
          >
            <div ref={searchResults.length === index + 1 ? lastItemRef : null}>
              <IconButton
                ariaLabel="select"
                className={alreadyIncluded ? styles.selectedButton : styles.selectButton}
                menuProps={selectMenuProps}
                iconProps={{ iconName }}
                menuIconProps={{ iconName: '' }}
                id={`iconButton${index}`}
                onMenuClick={
                  alreadyIncluded
                    ? event => event.preventDefault()
                    : event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onSelectItems(item);
                      }
                }
              />
            </div>
          </TooltipHost>
        );
      },
    },
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      ariaLabel: 'icon',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: item => {
        if (item.isList) {
          return (
            <div className={styles.iconColumn}>
              <FontIcon
                iconName="BulletedList"
                ariaLabel="BulletedList"
                className={styles.detailRowIconColor}
              />
            </div>
          );
        }
        return <div className={styles.iconColumn} />;
      },
    },
    {
      key: individualItemsColumnHeaders[listType]?.column1.key,
      name: individualItemsColumnHeaders[listType]?.column1.name,
      fieldName: individualItemsColumnHeaders[listType]?.column1.fieldName,
      isSorted: sortColumn === individualItemsColumnHeaders[listType]?.column1.fieldName,
      isSortedDescending: !sortAscending,
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: item => item.name,
    },
    {
      key: 'productId',
      name: 'Product ID',
      fieldName: 'productId',
      isMultiline: true,
      minWidth: 100,
      maxWidth: 200,
      columnActionsMode: 0,
      isPadded: true,
      onRender: item => {
        const otherProductIds = drop(get(item, 'productIds'));
        const productIdsToolTip = otherProductIds.map(product => (
          <span key={product}>
            {product} <br />
          </span>
        ));

        const moreProductIds = size(get(item, 'productIds')) - 1;
        return (
          <div className={styles.productText}>
            {get(item, 'productIds[0]')}
            {moreProductIds > 0 && (
              <TooltipHost
                className={styles.tooltipContent}
                content={productIdsToolTip}
                calloutProps={{
                  gapSpace: 5,
                }}
              >
                <span className={styles.moreProductIds}>
                  {' '}
                  <br /> +{moreProductIds} more
                </span>
              </TooltipHost>
            )}
          </div>
        );
      },
    },
    {
      key: individualItemsColumnHeaders[listType]?.column2.key,
      name: individualItemsColumnHeaders[listType]?.column2.name,
      fieldName: individualItemsColumnHeaders[listType]?.column2.fieldName,
      isSorted: sortColumn === dLTypeValues[listType]?.sortColumn,
      isSortedDescending: !sortAscending,
      minWidth: 185,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return `#${item.id}`;
      },
    },
  ];

  const listColumns = [
    {
      key: 'select',
      name: 'select',
      headerClassName: styles.iconColumnHeaderSelectButton,
      arianame: 'icon',
      iconName: selectAllItems ? 'SkypeCircleCheck' : '',
      isIconOnly: true,
      fieldName: 'select',
      minWidth: 20,
      maxWidth: 20,
      onColumnClick: onHandleColumnClick,
      onRender: (item, index) => {
        const productId = get(item, 'id');
        const iconName = selectedItems.some(selected => selected.id === productId)
          ? 'CompletedSolid'
          : '';
        const alreadyIncluded =
          listFormData && listFormData.listItems.some(selected => selected.id === productId);
        return (
          <TooltipHost
            onTooltipToggle={false}
            content={
              selectedItems.some(selected => selected.id === productId) ? `List Included` : ''
            }
            calloutProps={{
              gapSpace: 0,
            }}
          >
            <div ref={searchResults.length === index + 1 ? lastItemRef : null}>
              <IconButton
                ariaLabel="select"
                className={alreadyIncluded ? styles.selectedButton : styles.selectButton}
                menuProps={selectMenuProps}
                iconProps={{ iconName }}
                menuIconProps={{ iconName: '' }}
                id={`iconButton${index}`}
                onMenuClick={
                  alreadyIncluded
                    ? event => event.preventDefault()
                    : event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onSelectItems(item);
                      }
                }
              />
            </div>
          </TooltipHost>
        );
      },
    },
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      ariaLabel: 'icon',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon
              iconName="BulletedList"
              ariaLabel="BulletedList"
              className={styles.detailRowIconColor}
            />
          </div>
        );
      },
    },
    {
      key: listColumnHeaders[listType]?.column1.key,
      name: listColumnHeaders[listType]?.column1.name,
      fieldName: listColumnHeaders[listType]?.column1.fieldName,
      isSorted: sortColumn === listColumnHeaders[listType]?.column1.fieldName,
      isSortedDescending: !sortAscending,
      minWidth: 320,
      maxWidth: 762,
      isResizable: true,
      onColumnClick: handleColumnClick,
    },
  ];

  const stagingColumns = [
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      ariaLabel: 'icon',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: item => {
        if (item.isList) {
          return (
            <div className={styles.iconColumn}>
              <FontIcon
                iconName="BulletedList"
                ariaLabel="BulletedList"
                className={styles.detailRowIconColor}
              />
            </div>
          );
        }
        return <div className={styles.iconColumn} />;
      },
    },
    {
      key: stagingColumnHeaders[listType]?.column1.key,
      name: stagingColumnHeaders[listType]?.column1.name,
      fieldName: stagingColumnHeaders[listType]?.column1.fieldName,
      isSorted: sortColumn === stagingColumnHeaders[listType]?.column1.fieldName,
      isSortedDescending: !sortAscending,
      minWidth: 320,
      maxWidth: 600,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: item => item.name,
    },
    {
      key: stagingColumnHeaders[listType]?.column2.key,
      name: stagingColumnHeaders[listType]?.column2.name,
      fieldName: stagingColumnHeaders[listType]?.column2.fieldName,
      isSorted: sortColumn === dLTypeValues[listType]?.sortColumn,
      isSortedDescending: !sortAscending,
      minWidth: 185,
      maxWidth: 245,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: item => {
        if (item.isList) {
          return <div className={styles.andText} />;
        }
        return `#${item.id}`;
      },
    },
    {
      key: 'remove',
      name: 'Remove',
      arianame: 'remove',
      isIconOnly: true,
      fieldName: 'remove',
      headerClassName: styles.iconColumnHeader,
      minWidth: 25,
      maxWidth: 25,
      isPadded: true,
      onRender: (item, index) => {
        const iconName = 'Cancel';
        return (
          <IconButton
            className={styles.removeButton}
            ariaLabel="remove"
            menuProps={selectMenuProps}
            iconProps={{ iconName }}
            menuIconProps={{ iconName: '' }}
            id={`removeButton${index}`}
            onMenuClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onSelectItems(item);
            }}
          />
        );
      },
    },
  ];

  if (listType === 'product') {
    listColumns.push(viewListColumn);
    individualItemsColumns.push(viewIndividualItemsColumn);
    stagingColumns.splice(3, 0, viewStagingColumn);
  }

  return (
    <>
      <PageTitle>
        <Link to={{ pathname: previousPathName, listFormData }}>
          {listId ? 'Edit' : 'Create'} a List
        </Link>
        <span className={styles.slash}> / </span>
        {dimensionListTypes.find(e => e.key === listTypeTitle).text} Search
      </PageTitle>

      <Main>
        <div className={styles.searchFieldsContainer}>
          {filterStates.map((val, index) => (
            <>
              <div
                className={styles.filterAndSearchContainer}
                aria-label="filterAndSearchContainer"
              >
                <Dropdown
                  ariaLabel="Dropdown"
                  placeholder="Select"
                  selectedKey={filterStates[index].filter}
                  options={availableFilterOptions(index)}
                  className={styles.filter}
                  data-testid="dimensionListSearchDropdown"
                  styles={{
                    title: { backgroundColor: theme.palette.neutralWhite },
                    dropdown: { fontWeight: 'normal' },
                    dropdownItem: { fontWeight: 'normal' },
                  }}
                  onChange={(event, option) => {
                    handleFilter(event, option, index);
                  }}
                />
                <SearchBar
                  placeholder="Search"
                  ariaLabel="Search"
                  value={filterStates[index].query}
                  hasValue
                  underlined
                  onChange={query => {
                    handleSearchInput(query, index);
                  }}
                  onClear={() => {
                    handleSearchClear(index);
                  }}
                  data-testid="search-input"
                  className={styles.search}
                />
                {filterStates.length > 1 && (
                  <TextButton
                    disabled={filterStates.length === 1}
                    onClick={() => handleRemoveSearchFields(index)}
                  >
                    Remove
                  </TextButton>
                )}
              </div>
              {index !== filterStates.length - 1 && <Text className={styles.andText}>and</Text>}
            </>
          ))}
          {filterStates.length !== filterOptions[listType].length - 1 && (
            <TextButton
              noPadding
              className={styles.addSearchFieldButton}
              onClick={() => handleAdditionalSearchFields()}
              data-testid="dimensionListSearchAddField"
              disabled={filterStates.length > 1}
            >
              Add a Search Field
            </TextButton>
          )}
        </div>
        <div className={styles.resultsContainer}>
          {showSearchResults && !loading && (
            <>
              <div>
                <Text as="h4" block>
                  {searchResults.length || 'No'} Results
                </Text>
                {searchResults.length === 0 && (
                  <Text as="p" block className={styles.noResultsSentence}>
                    Try searching by {listType} name, {listType} ID, or list name
                  </Text>
                )}
              </div>
              <div className={styles.selectDeselectButtonsContainer}>
                {newlySelectedProducts.length === 0 && (
                  <TextButton
                    className={styles.textButton}
                    onClick={() => handleSelectAllButton()}
                    data-testid="select-all"
                    disabled={searchResults.length === 0}
                  >
                    Select All
                  </TextButton>
                )}
                {newlySelectedProducts.length > 0 && (
                  <TextButton
                    className={styles.textButton}
                    onClick={() => handleDeselectAndRemoveAll()}
                    data-testid="deselect-all"
                  >
                    Deselect All
                  </TextButton>
                )}
              </div>
            </>
          )}
        </div>
        {/* If someone searched but got 0 results, hide the list */}
        {showSearchResults && (searchResults.length !== 0 || loading) && (
          <Module>
            <ModuleContentList>
              <DimensionSearchList
                columns={isListSearch ? listColumns : individualItemsColumns}
                items={searchResults}
                selectedItems={selectedItems}
                onHandleColumnClick={onHandleColumnClick}
                disableRowLink
                shimmered
                enableView
                className={styles.productList}
                enableShimmer={loading}
              />
            </ModuleContentList>
          </Module>
        )}

        {/* Only show staging area if new items are selected */}
        {newlySelectedProducts.length !== 0 && (
          <div>
            <div className={styles.resultsContainer}>
              <div className={styles.resultsMessage}>
                <Text as="h4" block>
                  {newlySelectedProducts.length === 0
                    ? ''
                    : `${newlySelectedProducts.length} Selected ${
                        newlySelectedProducts.length > 1 ? 'Items' : 'Item'
                      } `}
                </Text>
              </div>
              <div className={styles.selectDeselectButtonsContainer}>
                <TextButton
                  className={styles.textButton}
                  onClick={() => handleDeselectAndRemoveAll()}
                  data-testid="remove-all"
                  disabled={searchResults.length === 0}
                >
                  Remove All
                </TextButton>
              </div>
            </div>
            <Module>
              <ModuleContentList>
                <DimensionSearchList
                  columns={stagingColumns}
                  items={newlySelectedProducts}
                  className={styles.productList}
                  disableRowLink
                  shimmered
                  enableView
                  enableShimmer={loading}
                  noBorderBottom
                />
              </ModuleContentList>
            </Module>
          </div>
        )}
        <div className={styles.bundleButtonsContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            data-testid="dimensionListSearchCancel"
          >
            Cancel
          </TextButton>
          {newlySelectedProducts.length !== 0 &&
            (!isSubmitting ? (
              <PrimaryButton
                type="submit"
                onClick={() => handleSave()}
                disabled={isSubmitting}
                data-testid="dimensionListSearchAddItems"
              >
                Add to List
              </PrimaryButton>
            ) : (
              <SavingIndicator />
            ))}
        </div>
      </Main>
    </>
  );
};

export default AddDimensionListSearch;
